/**
 * Geospatial utilities for optimizing map-based queries
 * This file contains helper functions for spatial indexing and optimization
 */

// Base32 alphabet for geohash encoding
const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

/**
 * Simple geohash implementation for spatial indexing
 * This is useful for creating compound indexes in Firestore
 */
export class GeoHash {
  static encode(latitude, longitude, precision = 9) {
    let latRange = [-90, 90];
    let lngRange = [-180, 180];
    let hash = '';
    let bits = 0;
    let bit = 0;
    let even = true;

    while (hash.length < precision) {
      if (even) {
        // longitude
        const mid = (lngRange[0] + lngRange[1]) / 2;
        if (longitude >= mid) {
          bit = (bit << 1) | 1;
          lngRange[0] = mid;
        } else {
          bit = bit << 1;
          lngRange[1] = mid;
        }
      } else {
        // latitude
        const mid = (latRange[0] + latRange[1]) / 2;
        if (latitude >= mid) {
          bit = (bit << 1) | 1;
          latRange[0] = mid;
        } else {
          bit = bit << 1;
          latRange[1] = mid;
        }
      }

      even = !even;
      if (++bits === 5) {
        hash += BASE32[bit];
        bits = 0;
        bit = 0;
      }
    }

    return hash;
  }

  static decode(hash) {
    let latRange = [-90, 90];
    let lngRange = [-180, 180];
    let even = true;

    for (let i = 0; i < hash.length; i++) {
      const char = hash[i];
      const cd = BASE32.indexOf(char);
      
      for (let bit = 4; bit >= 0; bit--) {
        const mask = 1 << bit;
        if (even) {
          // longitude
          const mid = (lngRange[0] + lngRange[1]) / 2;
          if (cd & mask) {
            lngRange[0] = mid;
          } else {
            lngRange[1] = mid;
          }
        } else {
          // latitude
          const mid = (latRange[0] + latRange[1]) / 2;
          if (cd & mask) {
            latRange[0] = mid;
          } else {
            latRange[1] = mid;
          }
        }
        even = !even;
      }
    }

    return {
      latitude: (latRange[0] + latRange[1]) / 2,
      longitude: (lngRange[0] + lngRange[1]) / 2
    };
  }

  /**
   * Get neighboring geohashes for expanding search area
   */
  static getNeighbors(hash) {
    // This is a simplified version - you might want to use a more complete implementation
    const decoded = this.decode(hash);
    const precision = hash.length;
    const delta = Math.pow(32, -(precision - 1));
    
    return [
      this.encode(decoded.latitude + delta, decoded.longitude, precision),
      this.encode(decoded.latitude - delta, decoded.longitude, precision),
      this.encode(decoded.latitude, decoded.longitude + delta, precision),
      this.encode(decoded.latitude, decoded.longitude - delta, precision),
    ];
  }
}

/**
 * Calculate distance between two geographical points using Haversine formula
 */
export function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Spatial clustering algorithm for reducing pin density
 */
export class SpatialClusterer {
  constructor(gridSize = 0.01) {
    this.gridSize = gridSize;
  }

  cluster(points, maxClusters = 100) {
    const clusters = new Map();
    
    points.forEach(point => {
      const cellKey = this.getCellKey(point.latitude, point.longitude);
      
      if (!clusters.has(cellKey)) {
        clusters.set(cellKey, []);
      }
      clusters.get(cellKey).push(point);
    });

    // Select representative points from each cluster
    const result = [];
    for (const [cellKey, cellPoints] of clusters) {
      // Sort by priority (e.g., number of jobs) and select the best representative
      cellPoints.sort((a, b) => (b.priority || 0) - (a.priority || 0));
      result.push(cellPoints[0]);
      
      if (result.length >= maxClusters) break;
    }

    return result;
  }

  getCellKey(lat, lng) {
    const cellLat = Math.floor(lat / this.gridSize) * this.gridSize;
    const cellLng = Math.floor(lng / this.gridSize) * this.gridSize;
    return `${cellLat.toFixed(6)},${cellLng.toFixed(6)}`;
  }
}

/**
 * Viewport-based query optimizer
 */
export class ViewportOptimizer {
  static getOptimalDensity(zoom, viewportArea) {
    // Define density levels based on zoom
    const densityLevels = [
      { minZoom: 0, maxZoom: 8, maxPins: 20, gridSize: 0.1 },    // Country/Regional
      { minZoom: 9, maxZoom: 11, maxPins: 50, gridSize: 0.05 },  // State/Province
      { minZoom: 12, maxZoom: 13, maxPins: 100, gridSize: 0.02 }, // City
      { minZoom: 14, maxZoom: 15, maxPins: 200, gridSize: 0.01 }, // Neighborhood
      { minZoom: 16, maxZoom: 20, maxPins: 500, gridSize: 0.005 } // Street
    ];

    const level = densityLevels.find(level => 
      zoom >= level.minZoom && zoom <= level.maxZoom
    ) || densityLevels[densityLevels.length - 1];

    return level;
  }

  static shouldRefetch(currentBounds, lastFetchBounds, zoom) {
    if (!lastFetchBounds) return true;
    
    // Calculate how much the viewport has changed
    const currentNE = currentBounds.getNorthEast();
    const currentSW = currentBounds.getSouthWest();
    const lastNE = lastFetchBounds.getNorthEast();
    const lastSW = lastFetchBounds.getSouthWest();
    
    // Use different thresholds based on zoom level
    const threshold = zoom >= 15 ? 0.3 : zoom >= 12 ? 0.5 : 0.7;
    
    // Check if the current viewport extends significantly beyond the last fetch area
    const latOverlap = Math.min(currentNE.lat(), lastNE.lat()) - Math.max(currentSW.lat(), lastSW.lat());
    const lngOverlap = Math.min(currentNE.lng(), lastNE.lng()) - Math.max(currentSW.lng(), lastSW.lng());
    
    const currentLatSpan = currentNE.lat() - currentSW.lat();
    const currentLngSpan = currentNE.lng() - currentSW.lng();
    
    const latOverlapRatio = latOverlap / currentLatSpan;
    const lngOverlapRatio = lngOverlap / currentLngSpan;
    
    return latOverlapRatio < threshold || lngOverlapRatio < threshold;
  }
}

/**
 * Cache manager for spatial data
 */
export class SpatialCache {
  constructor(maxSize = 100, maxAge = 10 * 60 * 1000) {
    this.cache = new Map();
    this.maxSize = maxSize;
    this.maxAge = maxAge;
  }

  generateKey(bounds, zoom, filters = {}) {
    const precision = zoom >= 15 ? 4 : zoom >= 12 ? 3 : 2;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    
    const boundsKey = `${ne.lat().toFixed(precision)},${ne.lng().toFixed(precision)}-${sw.lat().toFixed(precision)},${sw.lng().toFixed(precision)}`;
    const filtersKey = Object.keys(filters).sort().map(key => `${key}:${filters[key]}`).join('|');
    
    return `${boundsKey}-z${zoom}-${filtersKey}`;
  }

  get(key) {
    const entry = this.cache.get(key);
    if (entry && Date.now() - entry.timestamp < this.maxAge) {
      // Move to end (LRU)
      this.cache.delete(key);
      this.cache.set(key, entry);
      return entry.data;
    }
    if (entry) {
      this.cache.delete(key);
    }
    return null;
  }

  set(key, data) {
    // Remove oldest entries if cache is full
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
    
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clear() {
    this.cache.clear();
  }

  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      oldestEntry: this.cache.size > 0 ? 
        Math.min(...Array.from(this.cache.values()).map(entry => entry.timestamp)) : null
    };
  }
}

/**
 * Performance monitoring utilities
 */
export class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
  }

  startTimer(operationName) {
    this.metrics.set(operationName, {
      startTime: performance.now(),
      endTime: null,
      duration: null
    });
  }

  endTimer(operationName) {
    const metric = this.metrics.get(operationName);
    if (metric) {
      metric.endTime = performance.now();
      metric.duration = metric.endTime - metric.startTime;
      console.log(`🔍 ${operationName}: ${metric.duration.toFixed(2)}ms`);
    }
  }

  getMetrics() {
    return Object.fromEntries(this.metrics);
  }

  reset() {
    this.metrics.clear();
  }
}

// Export commonly used constants
export const ZOOM_LEVELS = {
  WORLD: 2,
  CONTINENT: 4,
  COUNTRY: 6,
  STATE: 8,
  CITY: 10,
  NEIGHBORHOOD: 12,
  STREET: 14,
  BUILDING: 16
};

export const DENSITY_CONFIGS = {
  [ZOOM_LEVELS.WORLD]: { maxPins: 10, gridSize: 1.0 },
  [ZOOM_LEVELS.CONTINENT]: { maxPins: 20, gridSize: 0.5 },
  [ZOOM_LEVELS.COUNTRY]: { maxPins: 30, gridSize: 0.2 },
  [ZOOM_LEVELS.STATE]: { maxPins: 50, gridSize: 0.1 },
  [ZOOM_LEVELS.CITY]: { maxPins: 100, gridSize: 0.05 },
  [ZOOM_LEVELS.NEIGHBORHOOD]: { maxPins: 200, gridSize: 0.02 },
  [ZOOM_LEVELS.STREET]: { maxPins: 400, gridSize: 0.01 },
  [ZOOM_LEVELS.BUILDING]: { maxPins: 800, gridSize: 0.005 }
};

// Geospatial utility functions for map operations
export const GeoUtils = {
  // Calculate approximate distance between two points in kilometers
  getDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  },

  toRad(value) {
    return value * Math.PI / 180;
  },

  // Calculate optimal pin density based on zoom level
  getPinDensity(zoom, mapArea) {
    if (zoom >= 16) return { maxPins: 500, gridSize: 0.001 }; // Street level
    if (zoom >= 14) return { maxPins: 300, gridSize: 0.003 }; // Neighborhood level  
    if (zoom >= 12) return { maxPins: 150, gridSize: 0.01 };  // City level
    if (zoom >= 10) return { maxPins: 75, gridSize: 0.03 };   // Metropolitan level
    return { maxPins: 30, gridSize: 0.1 };                    // Regional level
  },

  // Create grid cells for spatial indexing
  createGridCells(bounds, gridSize) {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const cells = [];

    for (let lat = sw.lat(); lat <= ne.lat(); lat += gridSize) {
      for (let lng = sw.lng(); lng <= ne.lng(); lng += gridSize) {
        cells.push({
          sw: { lat: lat, lng: lng },
          ne: { lat: Math.min(lat + gridSize, ne.lat()), lng: Math.min(lng + gridSize, ne.lng()) }
        });
      }
    }
    return cells;
  }
};

// Business photo generation utilities
export const BusinessPhotoUtils = {
  // Generate initials for business name (up to 2 letters)
  generateInitials(businessName) {
    if (!businessName) return 'R'; // Default for restaurant
    
    return businessName
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  },

  // Generate a consistent gradient background based on business name
  generateGradientStyle(businessName) {
    if (!businessName) {
      // Default restaurant gradient
      return {
        background: 'linear-gradient(135deg, #FF8C00, #FF6B00)',
        color: 'white'
      };
    }

    // Generate a hash from business name for consistent colors
    let hash = 0;
    for (let i = 0; i < businessName.length; i++) {
      const char = businessName.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Define color palettes for restaurants
    const colorPalettes = [
      ['#FF8C00', '#FF6B00'], // Orange gradient (default)
      ['#FF6B6B', '#FF5252'], // Red gradient
      ['#4ECDC4', '#44A08D'], // Teal gradient
      ['#45B7D1', '#96C93D'], // Blue-green gradient
      ['#F093FB', '#F5576C'], // Pink gradient
      ['#4facfe', '#00f2fe'], // Light blue gradient
      ['#43e97b', '#38f9d7'], // Green gradient
      ['#fad0c4', '#ffd1ff'], // Soft pink gradient
      ['#a8edea', '#fed6e3'], // Mint gradient
      ['#ffafbd', '#ffc3a0']  // Warm gradient
    ];

    // Select palette based on hash
    const paletteIndex = Math.abs(hash) % colorPalettes.length;
    const [color1, color2] = colorPalettes[paletteIndex];

    return {
      background: `linear-gradient(135deg, ${color1}, ${color2})`,
      color: 'white'
    };
  },

  // Generate automatic business photo as data URL (for canvas rendering)
  generateBusinessPhotoDataUrl(businessName, size = 200) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = size;
    canvas.height = size;

    // Get gradient colors
    const gradientStyle = this.generateGradientStyle(businessName);
    const initials = this.generateInitials(businessName);

    // Create gradient
    const gradient = ctx.createLinearGradient(0, 0, size, size);
    
    // Extract colors from gradient string
    const gradientMatch = gradientStyle.background.match(/linear-gradient\(135deg, (#[0-9A-Fa-f]{6}), (#[0-9A-Fa-f]{6})\)/);
    if (gradientMatch) {
      gradient.addColorStop(0, gradientMatch[1]);
      gradient.addColorStop(1, gradientMatch[2]);
    } else {
      // Fallback to orange gradient
      gradient.addColorStop(0, '#FF8C00');
      gradient.addColorStop(1, '#FF6B00');
    }

    // Fill background
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);

    // Add text
    ctx.fillStyle = 'white';
    ctx.font = `bold ${size * 0.4}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, size / 2, size / 2);

    // Return as data URL
    return canvas.toDataURL('image/png');
  },

  // Generate CSS style for inline gradient background
  generateInlineStyle(businessName) {
    const gradientStyle = this.generateGradientStyle(businessName);
    return {
      background: gradientStyle.background,
      color: gradientStyle.color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      fontWeight: 'bold',
      width: '100%',
      height: '100%'
    };
  }
}; 